import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '../../layout/icons';
import { stripEdges, stripPageTarget, stripRevealTarget } from '../filmstrip/overflow';
import { itemLabel, itemPosition, navigatorMeta, submissionItems, type StudentSubmission } from './submissions';
import '../../styles/submissions.css';

// width of the fade over each end of the strip, as .hw-nav__more
const MORE_WIDTH = 44;

/** The chip strip's hidden ends, and the scroll that keeps the shown chip in view. */
function useChipStrip(shown: number, revision: string) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = stripEdges(el.scrollLeft, el.clientWidth, el.scrollWidth);
    setEdges((current) => (current.start === next.start && current.end === next.end ? current : next));
  }, []);

  const page = useCallback((direction: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollLeft = stripPageTarget(el.scrollLeft, el.clientWidth, el.scrollWidth, direction);
    measure();
  }, [measure]);

  // before paint, so the next student never shows the last one's scroll position
  useLayoutEffect(() => {
    const el = ref.current;
    const chip = el ? (el.children[shown] as HTMLElement | undefined) : undefined;
    if (el && chip) el.scrollLeft = stripRevealTarget({ left: chip.offsetLeft, right: chip.offsetLeft + chip.offsetWidth }, el.scrollLeft, el.clientWidth, el.scrollWidth, MORE_WIDTH);
    measure();
  }, [shown, revision, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure, false);
    return () => window.removeEventListener('resize', measure, false);
  }, [measure]);

  return { ref, edges, measure, page };
}

export interface SubmissionNavigatorProps {
  taskTitle: string;
  /** Students who submitted, in roster order. */
  order: StudentSubmission[];
  current: StudentSubmission;
  item: number;
  onStep: (delta: number) => void;
  onSelectItem: (item: number) => void;
  /** Back to the đề. */
  onClose: () => void;
}

// The bar over the board while a student's homework replaces the đề: whose work, its parts, next/prev
export function SubmissionNavigator({ taskTitle, order, current, item, onStep, onSelectItem, onClose }: SubmissionNavigatorProps) {
  const items = submissionItems(current);
  const shown = Math.min(item, items.length - 1);
  const strip = useChipStrip(shown, current.id);
  const index = order.indexOf(current);
  const meta = navigatorMeta({ index, order, taskTitle });

  return (
    <div className="hw-nav" role="group" aria-label="Bài nộp của học sinh">
      <button type="button" className="hw-nav__step" aria-label="Học sinh trước" disabled={index <= 0} onClick={() => onStep(-1)}><ChevronLeftIcon size={22} /></button>
      <div className="hw-nav__who" aria-live="polite">
        <strong title={current.studentName}>{current.studentName}</strong>
        <span title={meta}>{meta}</span>
      </div>
      {items.length > 1 ? (
        <>
          <span className="hw-nav__count">{itemPosition(items, shown)}</span>
          <div className="hw-nav__strip">
            <div className="hw-nav__items" ref={strip.ref} onScroll={strip.measure} role="group" aria-label="Nội dung bài nộp">
              {items.map((_item, at) => (
                <button type="button" key={at} className={at === shown ? 'hw-nav__item is-active' : 'hw-nav__item'} aria-pressed={at === shown} onClick={() => onSelectItem(at)}>{itemLabel(items, at)}</button>
              ))}
            </div>
            {/* touch only: every chip is already in the tab order, and focus scrolls it into view */}
            {strip.edges.start ? <button type="button" className="hw-nav__more hw-nav__more--start" tabIndex={-1} aria-hidden={true} onClick={() => strip.page(-1)}><span className="hw-nav__more-glyph"><ChevronLeftIcon size={16} /></span></button> : null}
            {strip.edges.end ? <button type="button" className="hw-nav__more hw-nav__more--end" tabIndex={-1} aria-hidden={true} onClick={() => strip.page(1)}><span className="hw-nav__more-glyph"><ChevronRightIcon size={16} /></span></button> : null}
          </div>
        </>
      ) : null}
      <button type="button" className="hw-nav__step" aria-label="Học sinh tiếp theo" disabled={index < 0 || index >= order.length - 1} onClick={() => onStep(1)}><ChevronRightIcon size={22} /></button>
      <button type="button" className="hw-nav__close" aria-label="Đóng bài nộp, quay lại đề" title="Quay lại đề" onClick={onClose}><CloseIcon size={20} /></button>
    </div>
  );
}
